import { Languages } from "lucide-react";
import { useState } from "react";

import { queryKeys } from "@/api/hooks";
import {
  requiredLanguagesFor,
  useAssetSubstitutionsQuery,
  useLibraryPlacementRegistryQuery,
  type LibraryAsset,
} from "@/api/library";
import { isTerminalQueryError } from "@/api/queryClient";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import { lifecycleRefusal } from "@/lib/archiveRefusal";
import { newDraftId } from "@/lib/assetContent";
import {
  addRequiredLanguage,
  missingRequiredLanguages,
  updateNamedDocument,
} from "@/lib/namedParts";
import {
  findSubstitutionPart,
  languageProductName,
  projectionFor,
  readSubstitutionDocument,
  rulesLanguages,
  setSubstitution,
  substitutionBuckets,
  writeSubstitutionDocument,
  type RulesLanguageView,
} from "@/lib/rulesContent";
import { PageHeader } from "@/shell/PageHeader";
import { Button } from "@/ui/Button";
import { Notice } from "@/ui/Notice";
import { Tooltip } from "@/ui/Tooltip";
import { SaveStatusLine } from "@/ui/library/AssetEditor";
import {
  LifecycleDialog,
  type LifecycleMode,
} from "@/ui/library/LifecycleDialog";
import { LoadingState, PartialDataNotice, UnavailableState } from "@/ui/states";

import { NamedAssetMeta } from "./NamedAssetChrome";
import {
  NamedDocumentActions,
  StructuredDocumentUnavailable,
} from "./NamedDocumentEditor";
import {
  NamedConflictRecovery,
  NamedProblems,
  NamedRefusal,
} from "./NamedDraftStates";
import { NamedReadFailureState } from "./NamedReadFailure";
import { NewRulesDialog } from "./NewRulesDialog";
import {
  RulesLanguageContent,
  type SubstitutionsState,
} from "./RulesLanguageContent";
import { useNamedDraft, type NamedDraftEditor } from "./useNamedDraft";

/**
 * The canonical Rules detail, and its per-language content editor (#910).
 *
 * ## What the canonical reference shows, and what this has to reconcile
 *
 * [`/library/rules/english`](https://noproductcontent.lovable.app/library/rules/english)
 * is a named language page: a heading reading "English", a metadata strip with
 * Details, one large readable prompt field, and Back and Save. Its Rules library
 * lists "Hebrew" and "English" as two rows, which in the prototype are two
 * independent assets.
 *
 * THEY ARE NOT TWO ASSETS HERE, and the S7 screen contract says so in the same
 * line it links the mock: "keep one real bilingual asset identity even when
 * displaying language-specific content". `placement_registry` requires both `he`
 * and `en` on every version of a Rule, one project adoption pins the pair, and
 * the five pack files they compose to are `conversation_agent/{lang}/…` of ONE
 * asset. So the reference's per-language page becomes a per-language VIEW: the
 * asset lists the languages it carries, opening one gives exactly the
 * reference's screen — heading, metadata, Details, prompt field, Back, Save —
 * and the identity under both is one asset at one version.
 *
 * ## What is added to the reference, and why it is an addition
 *
 * The reference's single prompt field cannot express what MOD and NII actually
 * change. Their delta is `base_rules.txt` — which IS that prompt field — and
 * `substitutions.yaml`, four named tables of literal find/replace text that a
 * free-text prompt cannot encode. `SubstitutionTables` is that addition, and it
 * is stated as an addition rather than as a claim that the prototype has one.
 *
 * ## What this replaces, and why the generic editor could not stay
 *
 * `AssetEditor` reaches `PartsEditor`, whose "Part key" free-text input is the
 * manual-key escape hatch the 2026-08-25 amendment forbids and whose structured
 * field is a writable textarea over raw JSON. A clinician entering MOD's
 * substitutions there would be typing `he/substitutions` into a text box and
 * then hand-editing a four-hundred-entry JSON document — and would see only the
 * entries this asset overrides, with no way to tell those from the ones the
 * runtime inherits. Both are what this ticket removes.
 *
 * ## What it deliberately does NOT own
 *
 * The Speech profile and Hebrew pronunciation controls are #869/#870's, and the
 * Project Response-filtering control is #860's. Each lands beside these tables
 * or in `ProjectSectionPane`'s declared slots; nothing here restructures those
 * insertion points or claims their content.
 */
export function RulesAssetPage({ asset }: { asset: LibraryAsset }) {
  const state = useNamedDraft(asset);
  const write = useCapability("library.write");
  const restore = useCapability("library.restore");
  const registry = useLibraryPlacementRegistryQuery();
  const [openLanguage, setOpenLanguage] = useState<string | null>(null);
  const [lifecycle, setLifecycle] = useState<LifecycleMode | null>(null);
  const [copying, setCopying] = useState(false);

  const breadcrumbs = [
    { label: "Clinical library", to: "/library" },
    { label: "Rules", to: "/library/rules" },
  ];

  if (state.status === "loading") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <LoadingState label="Loading asset content" rows={4} />
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <NamedReadFailureState
          failure={state.failure}
          isPaused={state.isPaused}
          retry={state.retry}
          subject="Asset content"
          title="This asset's content could not be loaded"
          description={`The Editor Service did not return the content at ${asset.latestVersion}, so there is nothing to edit yet.`}
          deniedDescription="You are not authorized to read this asset's content."
          offlineDescription={`The content at ${asset.latestVersion} could not be reached, so there is nothing to edit yet. It loads on its own when the connection returns.`}
        />
      </>
    );
  }

  if (state.status === "unsupported") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <UnavailableState
          title="This asset's content is not a set of documents"
          description="Rules are authored as named language documents, and this asset's stored content is a different shape. It is shown by its own editor instead."
        />
      </>
    );
  }

  const { editor } = state;
  const languages = rulesLanguages(editor.parts, asset.assetType);
  const open =
    openLanguage === null
      ? null
      : (languages.find((entry) => entry.language === openLanguage) ?? null);

  // The language screen is a screen of its own, reached by Open and returned
  // from by Back — the canonical arrangement, and the reason the composition
  // read below it is mounted only where it is rendered rather than fetched on
  // the list this page opens with.
  if (open) {
    return (
      <RulesLanguageScreen
        asset={asset}
        editor={editor}
        view={open}
        breadcrumbs={breadcrumbs}
        onBack={() => setOpenLanguage(null)}
      />
    );
  }

  const archiveMode: LifecycleMode = asset.capabilities.archived
    ? "unarchive"
    : "archive";
  // Direction-specific caller gate (#647): archiving is `library.write`,
  // restoring is the admin/operations `library.restore` the ratified contract
  // (§2.1) reserves — the same split the service enforces on the two routes.
  const archiveRefusal = lifecycleRefusal(
    asset,
    archiveMode === "unarchive" ? restore.reason : write.reason,
  );
  const archiveLabel = archiveMode === "archive" ? "Archive" : "Unarchive";

  // The service's own list, never a guess: `FamilyPolicy.languages.required` off
  // the placement registry, which is the same projection `create_version`'s gate
  // reads. An unread registry says nothing rather than claiming a requirement.
  const required = requiredLanguagesFor(registry.data, asset.assetType);
  const missing = editor.readOnly
    ? []
    : missingRequiredLanguages(editor.parts, required);

  return (
    <>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={asset.name}
        description={`${languages.length} ${languages.length === 1 ? "language" : "languages"}`}
        meta={
          <NamedAssetMeta
            asset={asset}
            version={editor.pinned}
            identity={`rules/${asset.id}`}
          />
        }
        actions={
          <>
            {asset.capabilities.seeded ? (
              <Tooltip content="Part of the base clinical library. The original stays as it is — make an editable copy to create your own version.">
                <Button variant="outline" onClick={() => setCopying(true)}>
                  Create editable copy
                </Button>
              </Tooltip>
            ) : null}
            <Tooltip content={archiveRefusal ?? `${archiveLabel} this asset`}>
              <Button
                variant="outline"
                disabled={archiveRefusal !== null}
                onClick={() => setLifecycle(archiveMode)}
              >
                {archiveLabel}
              </Button>
            </Tooltip>
          </>
        }
      />

      <div className="space-y-4">
        <NamedConflictRecovery editor={editor} />
        <NamedRefusal editor={editor} />
        <NamedProblems editor={editor} />

        <ol
          aria-label="Languages this rule carries"
          className="overflow-hidden rounded-md border border-border bg-card"
        >
          {languages.map((view) => (
            <li
              key={view.language}
              className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 last:border-b-0"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-foreground">
                  {view.label}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {describeLanguage(view)}
                </span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpenLanguage(view.language)}
              >
                Open
              </Button>
            </li>
          ))}
        </ol>

        {missing.length > 0 ? (
          // THE REFUSAL AND ITS REMEDY, before the Save that would otherwise
          // report it. The service refuses a Rule missing a required language at
          // both author call sites (#635); this states the requirement in the
          // registry's own vocabulary and offers the one action that resolves
          // it — copying every document this asset already has into the missing
          // language, so the next Save lands on documents a clinician can see.
          //
          // It does NOT block Save. The service decides, and a surface that
          // refused content the server accepts would be a second gate.
          <Notice tone="warning">
            <p>
              A rule has to carry {required.map(languageProductName).join(" and ")}. Missing:{" "}
              {missing.map(languageProductName).join(", ")}.
            </p>
            {editor.readOnly ? null : (
              <div className="mt-2 flex flex-wrap gap-2">
                {missing.map((language) => (
                  <Button
                    key={language}
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      editor.change(
                        addRequiredLanguage(editor.parts, language, newDraftId),
                      )
                    }
                  >
                    <Languages aria-hidden="true" className="h-4 w-4" />
                    Add {languageProductName(language)} content
                  </Button>
                ))}
              </div>
            )}
          </Notice>
        ) : null}

        {editor.readOnly ? null : (
          <div className="flex flex-wrap items-center justify-end gap-3">
            <SaveStatusLine
              status={
                editor.saving
                  ? "saving"
                  : editor.failure !== null
                    ? "error"
                    : editor.dirty
                      ? "dirty"
                      : editor.lastSavedAt
                        ? "saved"
                        : "clean"
              }
              lastSavedAt={editor.lastSavedAt}
            />
            <Button
              variant="outline"
              disabled={!editor.dirty || editor.saving}
              onClick={editor.discard}
            >
              Discard
            </Button>
            <Button
              disabled={!editor.dirty || editor.saving}
              onClick={() => editor.save()}
            >
              Save
            </Button>
          </div>
        )}
      </div>

      {lifecycle ? (
        <LifecycleDialog
          asset={asset}
          mode={lifecycle}
          onClose={() => setLifecycle(null)}
        />
      ) : null}
      {copying ? (
        <NewRulesDialog copyFrom={asset} onClose={() => setCopying(false)} />
      ) : null}
    </>
  );
}

/** What one language row says it holds, without naming a role vocabulary. */
function describeLanguage(view: RulesLanguageView): string {
  const parts: string[] = [];
  if (view.prompts.length > 0) {
    parts.push(
      `${view.prompts.length} ${view.prompts.length === 1 ? "prompt" : "prompts"}`,
    );
  }
  if (view.hasSubstitutions) {
    parts.push("substitutions");
  }
  if (view.structured.length > 0) {
    parts.push(
      `${view.structured.length} clinical engine ${view.structured.length === 1 ? "document" : "documents"}`,
    );
  }
  return parts.length === 0 ? "No content yet" : parts.join(" · ");
}

/**
 * One language, open: the canonical named content screen (#910).
 *
 * The header is the reference's — heading, metadata strip with Details, Back and
 * Save — and the body is the prompt fields plus the named substitution tables.
 * Extracted so the substitutions read, which is per VERSION and per language,
 * is mounted only where it is rendered rather than fetched on the list screen
 * that never shows it.
 */
function RulesLanguageScreen({
  asset,
  editor,
  view,
  breadcrumbs,
  onBack,
}: {
  asset: LibraryAsset;
  editor: NamedDraftEditor;
  view: RulesLanguageView;
  breadcrumbs: { label: string; to?: string }[];
  onBack: () => void;
}) {
  // AT THE DRAFT'S PIN, not at the asset's head. The draft was opened at
  // `editor.pinned` and Save publishes against it, so a projection of a newer
  // version would describe a document this screen is not editing — and after a
  // save the pin moves, the key changes, and the new head's composition is
  // fetched. Base canon does not move under either.
  const substitutions = useAssetSubstitutionsQuery(asset.id, editor.pinned);
  const retrySubstitutions = useRetryPausedQuery(
    queryKeys.assetVersionSubstitutions(asset.id, editor.pinned),
  );

  const unread = substitutions.isPaused || substitutions.isError;
  const failure = substitutions.isPaused
    ? substitutions.failureReason
    : substitutions.error;

  const part = findSubstitutionPart(editor.parts, view.language);
  const document = readSubstitutionDocument(part);
  const language = projectionFor(substitutions.data, view.language);
  // THE TABLES ARE AUTHORABLE ONLY WHEN THE COMPOSITION IS KNOWN, and failing
  // open here was the defect. Editing them re-serialises the whole document, so
  // a table built from the draft alone — while the read is pending, or after it
  // failed — is a table whose Save replaces the stored document with whatever
  // this browser could make of it. The unreadable verdict arriving AFTERWARDS
  // does not undo that: the draft is already dirty and still savable.
  //
  // So the state is four-valued and only one of them authors. `unreadable` is
  // the service's verdict about the stored documents; the other two are facts
  // about this read, and both withhold the tables rather than guessing.
  const substitutionsState: SubstitutionsState = substitutions.isSuccess
    ? language?.unreadable
      ? {
          kind: "unreadable",
          source: language.unreadable,
          baseAsset: substitutions.data?.inheritance === "base",
        }
      : { kind: "ready" }
    : unread
      ? { kind: "unavailable" }
      : { kind: "loading" };
  const buckets =
    substitutionsState.kind === "ready"
      ? substitutionBuckets(language, document, {
          isBase: substitutions.data?.inheritance === "base",
        })
      : [];


  return (
    <>
      <PageHeader
        breadcrumbs={[
          ...breadcrumbs,
          { label: asset.name, to: `/library/rules/${asset.id}` },
          { label: view.label },
        ]}
        title={view.label}
        description="Language content"
        meta={
          <NamedAssetMeta
            asset={asset}
            version={editor.pinned}
            identity={`rules/${asset.id}`}
          />
        }
        actions={
          <NamedDocumentActions
            readOnly={editor.readOnly}
            dirty={editor.dirty}
            saving={editor.saving}
            onBack={onBack}
            onSave={() => editor.save()}
          />
        }
      />

      <div className="space-y-6">
        <NamedConflictRecovery editor={editor} />
        <NamedRefusal editor={editor} />
        <NamedProblems editor={editor} />

        {/* A REFUSAL IN A TOOLTIP IS NOT A REPORTED FAILURE, and an unread
            composition is not an empty one. Without the service's answer the
            inherited half of every table is unknown, so the tables state that
            rather than presenting this document's own entries as the whole
            runtime map. */}
        <PartialDataNotice
          visible={unread}
          description="The Editor Service did not return which substitutions this rule inherits from the base library. The tables are not shown and cannot be edited until it does — editing them without that answer would replace the stored document with what this page could make of it. Everything else on this page is unaffected."
          {...(unread && !isTerminalQueryError(failure)
            ? {
                action: (
                  <Button variant="outline" onClick={retrySubstitutions}>
                    Try again
                  </Button>
                ),
              }
            : {})}
        />

        <RulesLanguageContent
          view={view}
          buckets={buckets}
          genderForms={language?.genderForms ?? []}
          substitutions={substitutionsState}
          inheritance={substitutions.data?.inheritance ?? null}
          readOnly={editor.readOnly}
          readOnlyReason={editor.readOnlyReason}
          onPrompt={(documentId, text) =>
            editor.change(updateNamedDocument(editor.parts, documentId, text))
          }
          onSubstitution={(bucket, find, replacement) =>
            editor.change(
              writeSubstitutionDocument(
                editor.parts,
                view.language,
                setSubstitution(document, bucket, find, replacement),
                newDraftId,
              ),
            )
          }
        />

        {view.structured.length > 0 ? (
          <StructuredDocumentUnavailable count={view.structured.length} />
        ) : null}
      </div>
    </>
  );
}
